import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import video from '../assets/video/verse_homepage_loop.mp4';

const AIAudioSummarizerHero = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      title: "AI Audio Summarizer & AI Note Taker – Convert Audio, Video & YouTube Content Into Smart Notes",
      description: (
        <>
          Experience our <strong>AI audio summarizer</strong> and <strong>AI note taker</strong> to instantly process audio and video files. Convert <strong>audio recordings</strong> to text with our <strong>audio transcript generator</strong> and generate clear <strong>AI summaries</strong> highlighting key points. Summarize audio from <strong>team meetings</strong>, lectures, podcasts, and presentations effortlessly. Upload files in multiple formats securely with SSL protection, and <strong>save time</strong> with one click processing. Try our <strong>AI meeting summarizer</strong> and <strong>AI podcast summarizer</strong> today, <a href="https://audiosum.app/signup" className="text-decoration-none" style={{color: '#6C3AED'}}>sign up for free</a>!
        </>
      ),
      badges: [
        { text: "✓ Process Up to 5 Files at Once", bg: "bg-success" },
        { text: "✓ 99% Accuracy", bg: "bg-primary" },
        { text: "✓ 95+ Languages", bg: "bg-info" },
        { text: "✓ Instant Results", bg: "bg-warning text-dark" },
        { text: "✓ 7-Day Free Trial", bg: "bg-secondary" }
      ],
      buttons: {
        primary: "Try for Free!",
        secondary: "How it Works"
      },
      trustText: "Trusted by 1000+ professionals • ⭐ 4.8/5 rating • 🛡️ Secure & Private"
    },
    ar: {
      title: "لخص - نظام الذكاء الاصطناعي المتطور لتلخيص وتحليل الملفات الصوتية والفيديو والمستندات",
      subtitle: "حول ملفاتك الصوتية ومقاطع الفيديو إلى ملخصات ذكية ونصوص مكتوبة في ثوانٍ. استفد من قوة الذكاء الاصطناعي المتقدم لتحويل المحتوى الصوتي والمرئي إلى ملخصات نصية دقيقة وشاملة. منصتنا تدعم جميع تنسيقات الملفات الشائعة للصوت والفيديو، وتوفر تحليلاً ذكياً للمحتوى مع استخراج النقاط الرئيسية. سواء كنت تعمل على تحليل البودكاست، تسجيلات الاجتماعات، أو المحاضرات الأكاديمية، ستحصل على نتائج احترافية بسرعة وكفاءة عالية. ابدأ تجربتك المجانية اليوم.",
      description: (
        <>
          منصة لتلخيص النصوص وتحليل المحتوى بالذكاء الاصطناعي. حمل ملفك الصوتي، الفيديو، محاضره أو سجل المحاضرة مباشرةً وإحصل على ملخص ونقاط رئيسية مفصلة في دقائق. حمل أي PDF أو صورة، وإحصل على نتائج سريعة ومفصلة. تحويل ملخصاتك إلى بوربوينت جاهزة مع صور HD. إسأل مساعد الذكاء الاصطناعي أي سؤال عن ملفاتك وإحصل على إجابة ومساعدة فورية.
        </>
      ),
      badges: [
        { text: "✓ معالجة ملفات متعددة في نفس الوقت", bg: "bg-success" },
        { text: "✓ دقة عالية", bg: "bg-primary" },
        { text: "✓ أكثر من 95 لغة", bg: "bg-info" },
        { text: "✓ نتائج فورية", bg: "bg-warning text-dark" },
        { text: "✓ تجربة مجانية 7 أيام", bg: "bg-secondary" }
      ],
      buttons: {
        primary: "جرب مجاناً الآن!",
        secondary: "كيف يعمل"
      },
      trustText: "موثوق من قبل أكثر من 1000 محترف • ⭐ تقييم 4.8/5 • 🛡️ آمن وخاص"
    }
  };

  const currentContent = content[language];

  return (
    <div>
      <main className="page_main">
        <header className="hero_wrap" style={{ backgroundColor: '#F8FAFC' }}>
          <div className="g_visual_wrap u-position-relative u-cover-absolute" style={{display: 'none'}}>
            <div className="g_visual_background u-cover-absolute">
            </div>
            <video src={video} autoPlay={false} loop={true} muted={true} preload="none" playsInline={true} data-video="playpause" className="g_visual_video g_visual_video_hero u-cover-absolute"></video>
          </div>
          <div data-wf--global-section-space--section-space="main" className="g_section_space w-variant-60a7ad7d-02b0-6682-95a5-2218e6fd1490">
          </div>
          <div className="hero_contain u-container">
            <div className="hero_layout" style={{ maxWidth: '120ch' }}>
              <h1 style={{
                fontFamily: '"Playfair Display", serif',
                fontSize: 'clamp(36px, 5vw, 62px)',
                fontWeight: '700',
                lineHeight: '1.15',
                color: '#0F172A',
                marginBottom: '24px',
                letterSpacing: '-0.02em',
                paddingTop: '20px'
              }} data-aos="fade-down" data-aos-duration="1000">
                {language === 'ar'
                  ? <>{currentContent.title}</>
                  : <>AI Audio Summarizer & AI Note Taker – Convert Audio, Video & YouTube Content Into{' '}
                    <span style={{
                      background: 'linear-gradient(135deg, #6C3AED, #0EA5E9)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}>Smart Notes</span></>
                }
              </h1>
              <p className="u-text-style-lead" dir={language === 'ar' ? 'rtl' : 'ltr'} style={{ color: '#475569', fontFamily: '"Inter", sans-serif', lineHeight: '1.8', textAlign: 'center', fontSize: '18px', maxWidth: '680px', margin: '0 auto 36px' }} data-aos="fade-up" data-aos-delay="200">
                {currentContent.description}
              </p>

              {/* Badges Section - Glass Morphism Design */}
              <div className="mb-4" style={{ 
                marginTop: '30px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '10px',
                justifyContent: 'center'
              }}>
                {currentContent.badges.map((badge, index) => {
                  const gradients = [
                    {
                      bg: 'rgba(102, 126, 234, 0.15)',
                      border: 'rgba(102, 126, 234, 0.3)',
                      glow: 'rgba(102, 126, 234, 0.2)'
                    },
                    {
                      bg: 'rgba(16, 185, 129, 0.15)',
                      border: 'rgba(16, 185, 129, 0.3)',
                      glow: 'rgba(16, 185, 129, 0.2)'
                    },
                    {
                      bg: 'rgba(245, 158, 11, 0.15)',
                      border: 'rgba(245, 158, 11, 0.3)',
                      glow: 'rgba(245, 158, 11, 0.2)'
                    },
                    {
                      bg: 'rgba(239, 68, 68, 0.15)',
                      border: 'rgba(239, 68, 68, 0.3)',
                      glow: 'rgba(239, 68, 68, 0.2)'
                    },
                    {
                      bg: 'rgba(139, 92, 246, 0.15)',
                      border: 'rgba(139, 92, 246, 0.3)',
                      glow: 'rgba(139, 92, 246, 0.2)'
                    }
                  ];
                  
                  const currentGradient = gradients[index] || gradients[0];
                  
                  return (
                    <span 
                      key={index}
                      className="badge"
                      style={{
                        background: currentGradient.bg,
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        border: `1px solid ${currentGradient.border}`,
                        borderRadius: '10px',
                        color: '#0F172A',
                        padding: '8px 16px',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        flex: '0 0 auto',
                        boxShadow: `0 4px 15px ${currentGradient.glow}`,
                        whiteSpace: 'nowrap',
                        transition: 'all 0.3s ease',
                        cursor: 'default'
                      }}
                      data-aos="zoom-in" 
                      data-aos-delay={400 + (index * 50)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-3px) scale(1.05)';
                        e.currentTarget.style.boxShadow = `0 8px 25px ${currentGradient.glow}`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                        e.currentTarget.style.boxShadow = `0 4px 15px ${currentGradient.glow}`;
                      }}
                    >
                      {badge.text}
                    </span>
                  );
                })}
              </div>

              <div className="u-display-contents">
                <div className="u-btn-group u-text-style-h2" style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }} data-aos="fade-up" data-aos-delay="800">
                  <div data-wf--btn-primary--style="base" className="btn_primary_wrap">
                    <div className="g_clickable_wrap">
                      <a target="_blank" rel="noopener noreferrer" href={`https://audiosum.app/signup?lang=${language}`} className="g_clickable_link w-inline-block">
                        <span className="g_clickable_text u-sr-only">{currentContent.buttons.primary}</span>
                      </a>
                      <button type="button" className="g_clickable_btn">
                        <span className="g_clickable_text u-sr-only">{currentContent.buttons.primary}</span>
                      </button>
                    </div>
                    <div aria-hidden="true" className="btn_primary_text u-weight-medium u-text-style-main">{currentContent.buttons.primary}</div>
                    <div className="btn_primary_icon">
                      <div>
                        <svg xmlns="http://www.w3.org/2000/svg" width="100%" viewBox="0 0 24 24" fill="none" className="g_svg">
                          <circle cx="12" cy="12" r="11.5" stroke="currentColor">
                          </circle>
                          <path d="M7.11114 12L16.8889 12" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"></path>
                          <path d="M13.3334 15.5556L16.8889 12L13.3334 8.44446" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"></path>
                        </svg>
                      </div>
                    </div>
                  </div>
                  
                  {/* How it Works Button */}
                  <a
                    href="#how-it-works"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '14px 36px',
                      background: '#F8FAFC',
                      color: '#0F172A',
                      borderRadius: '9999px',
                      fontSize: '16px',
                      fontWeight: '600',
                      textDecoration: 'none',
                      border: '1px solid #E2E8F0',
                      transition: 'all 0.3s ease',
                      fontFamily: '"Inter", sans-serif'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#F1F5F9';
                      e.currentTarget.style.borderColor = '#CBD5E1';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#F8FAFC';
                      e.currentTarget.style.borderColor = '#E2E8F0';
                    }}
                  >
                    {currentContent.buttons.secondary} <i className="fas fa-arrow-down" style={{ fontSize: '14px' }}></i>
                  </a>
                </div>
              </div>
              
              {/* Trust Indicators */}
              <p className="u-text-style-small" style={{ marginTop: '30px', color: '#94A3B8' }} data-aos="fade-up" data-aos-delay="900">
                {currentContent.trustText}
              </p>
            </div>
          </div>
        </header>
      </main>
    </div>
  );
};

export default AIAudioSummarizerHero;